/**
 * When BOB takes a new release (Mark, 2026-10-16). BOB has no service worker (it is not a PWA, unlike
 * the barcoding site), so plugins/new-release.client.ts asks for the build's marker every few
 * minutes and this decides when a newer one may be loaded. The rule: never in the middle of
 * someone's work. A move to another screen is a safe moment (what was on this one is being left
 * anyway), and so is a screen nobody has touched for a while, with no window open and nothing typed.
 */

/** How often the marker is asked for while BOB is on screen. */
export const RELEASE_CHECK_EVERY_MS = 5 * 60_000
/** Asked again on coming back to the tab, but not more often than this. */
export const RELEASE_CHECK_AT_MOST_EVERY_MS = 30_000
/** How long nobody has touched the screen before it counts as idle. */
export const IDLE_BEFORE_RELOAD_MS = 2 * 60_000

/** The build's marker as Nuxt writes it (`/_nuxt/builds/latest.json`). */
export interface BuildMarker {
  id: string
}

/** True when the marker names a build other than the one running. A failed or odd answer is no news. */
export function isNewerBuild(runningId: string | null | undefined, marker: unknown): boolean {
  if (!runningId || runningId === 'dev') return false
  const id = (marker as BuildMarker | null)?.id
  return typeof id === 'string' && id.length > 0 && id !== runningId
}

/** What the screen is doing at the moment the rule is asked. */
export interface ScreenState {
  /** A newer build than the running one is out. */
  newRelease: boolean
  /** A window (dialog) is open. */
  windowOpen: boolean
  /** Something has been typed or picked on this screen since it was opened. */
  typedOnScreen: boolean
  /** How long since the last key, tap, click or scroll. */
  idleMs: number
  /** The tab is in the background. */
  hidden: boolean
}

/**
 * Someone is in the middle of something on this screen. The cursor merely standing in a field is not
 * (the Log In page puts it there by itself); typing or picking something is.
 */
export function isMidEdit(s: ScreenState): boolean {
  return s.windowOpen || s.typedOnScreen
}

/**
 * May the page reload on its own now (no navigation)? Only with a new release, nothing open or
 * typed, and either nobody has touched the screen for {@link IDLE_BEFORE_RELOAD_MS} or the tab is
 * in the background.
 */
export function mayReloadNow(s: ScreenState): boolean {
  if (!s.newRelease) return false
  if (isMidEdit(s)) return false
  return s.hidden || s.idleMs >= IDLE_BEFORE_RELOAD_MS
}

/**
 * May a move to another screen be done as a full load of the new release? Yes whenever a new release
 * is out and the move leaves this screen (another path). A change only of the address's query (a
 * filter, a page of a list, a settings tab) stays on the screen, so it is not a safe moment.
 */
export function mayReloadOnNavigate(newRelease: boolean, fromPath: string, toPath: string): boolean {
  return newRelease && fromPath !== toPath
}
